import { Avatar, Body } from '@assembly-js/design-system';
import { ButtonBase } from '@mui/material';
import React, { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { ChevronDownIcon } from 'src/components/icons';
import { User } from 'src/constants';
import { Loader } from 'src/legacy/components/Loading';
import { SelectDropDown } from 'src/legacy/components/Select/SelectDropdown';
import { SelectOption } from 'src/legacy/components/Select/types';
import { RootState } from 'src/store';
import {
  GraySmall,
  HoverBackground,
  HoverBorder,
  NonHoverBorder,
} from 'src/theme/colors';
import { getInitials, isActive } from 'src/utils/UserUtils';

const itemRenderer = (option: SelectOption) => {
  return (
    <div className="flex items-center gap-2">
      <Avatar
        src={option.avatarUrl ?? ''}
        fallbackColor={option.fallbackColor}
        text={getInitials(option.label, {
          twoLetters: true,
        })}
        variant="circle"
        size="sm"
        alt={option.label}
      />
      <Body size="sm" className="text-black-heading">
        {option.label}
      </Body>
    </div>
  );
};
const useStyles = makeStyles<{ width?: string | number }>()((theme, props) => ({
  inputContainer: {
    width: props.width || 'initial',
    minHeight: 46,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${NonHoverBorder}`,
    borderRadius: theme.shape.borderRadius,
  },
  inputContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  selected: {
    backgroundColor: HoverBackground,
    borderColor: HoverBorder,
  },
  dropdownIcon: {
    height: 12,
    width: 12,
    color: GraySmall,
  },
  '@keyframes rotateClockWise': {
    '0%': {
      transform: 'rotate(0)',
    },
    '100%': {
      opacity: 1,
      transform: 'rotate(180deg)',
    },
  },
  reversedIcon: {
    animation: `$rotateClockWise 500ms ${theme.transitions.easing.easeInOut}`,
    transform: 'rotate(180deg)',
  },
}));

interface WelcomeMessageSenderInputProps {
  width?: string | number;
  senderId?: string;
  onInputChanged: (value: string) => void;
}
export const WelcomeMessageSenderInput: React.FC<
  WelcomeMessageSenderInputProps
> = ({ width, senderId, onInputChanged }) => {
  const { internalUsers, isSettingsLoading, isSettingsLoaded } = useSelector(
    (state: RootState) => ({
      internalUsers: state.users.internalUsers,
      isSettingsLoading: state.settings.loading,
      isSettingsLoaded: state.settings.loaded,
    }),
    shallowEqual,
  );
  const { classes } = useStyles({ width });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [options, setOptions] = React.useState<SelectOption[]>([]);
  const handleInputClicked = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const dropDownOpen = Boolean(anchorEl);

  // setup options based on users in store
  React.useEffect(() => {
    if (internalUsers) {
      const filteredInternals = internalUsers.filter(
        (u) => isActive(u) && !u.isClientAccessLimited,
      );
      const internalUsersStaged: Array<SelectOption> = filteredInternals.map(
        (internalUser) => {
          const { givenName, familyName, avatarImageUrl, fallbackColor } =
            internalUser.fields;
          return {
            label: `${givenName} ${familyName}`,
            avatarUrl: avatarImageUrl,
            id: internalUser.id,
            fallbackColor,
          };
        },
      );

      setOptions(internalUsersStaged);
    }
  }, [internalUsers]);

  const [welcomeMessageSender, setWelcomeMessageSender] =
    React.useState<User | null>(null);

  useEffect(() => {
    if (!senderId) return;
    const sender = internalUsers.find(
      (internalUser) => internalUser.id === senderId,
    );
    if (!sender) return;
    setWelcomeMessageSender(sender);
  }, [internalUsers, senderId]);

  if (isSettingsLoading && !isSettingsLoaded) {
    return <Loader />;
  }

  const { avatarImageUrl, givenName, familyName, fallbackColor } =
    welcomeMessageSender?.fields || {};

  return (
    <>
      <ButtonBase
        aria-label="caret options"
        aria-controls="caret-options-menu"
        aria-haspopup="true"
        aria-describedby="welcome-message-sender-input"
        id="welcome-message-sender-input"
        onClick={handleInputClicked}
        className={`${classes.inputContainer} ${
          dropDownOpen ? classes.selected : ''
        }`}
      >
        <div className={classes.inputContent}>
          <Avatar
            src={avatarImageUrl}
            fallbackColor={fallbackColor}
            text={getInitials(`${givenName} ${familyName}`, {
              twoLetters: true,
            })}
            size="sm"
            variant="circle"
            alt={`${givenName} ${familyName}`}
            className="mr-2"
          />
          <Body size="sm" className="text-black-heading">
            {givenName} {familyName}
          </Body>
        </div>
        <ChevronDownIcon
          className={`${classes.dropdownIcon} ${
            dropDownOpen ? classes.reversedIcon : ''
          }`}
        />
      </ButtonBase>
      <SelectDropDown
        width={width}
        id="welcome-message-sender-select"
        open={dropDownOpen}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={(event) => {
          event.preventDefault();
          setAnchorEl(null);
        }}
        noOptionsText="No options"
        placeholder="Select a sender"
        isMultiple={false}
        options={options}
        // eslint-disable-next-line react/no-unstable-nested-components
        customOptionRenderer={(props, option) => (
          <li {...props}>{itemRenderer(option)}</li>
        )}
        onChange={(_, newValue) => {
          setAnchorEl(null);
          // onChange for Select can return a single value or an array
          // we only want to handle the single value case because
          // the welcome message sender is only a single select
          if (!newValue || Array.isArray(newValue)) return;
          onInputChanged(newValue.id);
        }}
      />
    </>
  );
};
